import { z } from "zod";

export const PARTNER_TABS = ["channels", "returns", "callbacks", "banners", "terms", "privacy"] as const;
export type PartnerTab = (typeof PARTNER_TABS)[number];
export type ChannelStatus = "" | "ACTIVE" | "INACTIVE";
export type ActiveFilter = "" | "true" | "false";

const optionalDate = z.string().refine((value) => value === "" || /^\d{4}-\d{2}-\d{2}$/.test(value), "유효한 날짜를 선택해 주세요.");
const localDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "일시를 선택해 주세요.");
const optionalLocalDateTime = z.string().refine((value) => value === "" || /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value), "유효한 일시를 선택해 주세요.");

export const channelFormSchema = z.object({
  channelCode: z.string().trim().regex(/^[A-Za-z0-9_-]{1,50}$/, "영문, 숫자, 밑줄, 하이픈으로 50자 이하로 입력해 주세요."),
  channelName: z.string().trim().min(1, "채널명을 입력해 주세요.").max(100, "채널명은 100자 이하로 입력해 주세요."),
  status: z.enum(["ACTIVE", "INACTIVE"]),
  contractStartedOn: optionalDate,
  contractEndedOn: optionalDate,
  description: z.string().trim().max(1000, "설명은 1,000자 이하로 입력해 주세요."),
}).superRefine((value, context) => {
  if (value.contractStartedOn && value.contractEndedOn && value.contractStartedOn > value.contractEndedOn) context.addIssue({ code: "custom", path: ["contractEndedOn"], message: "계약 종료일은 시작일 이후여야 합니다." });
});
export type ChannelFormValues = z.infer<typeof channelFormSchema>;

export const returnUrlFormSchema = z.object({
  scheme: z.enum(["http", "https"]),
  host: z.string().trim().min(1, "호스트를 입력해 주세요.").max(255, "호스트는 255자 이하로 입력해 주세요.").refine((value) => !/[/:?#\s]/.test(value), "프로토콜과 경로를 제외한 호스트만 입력해 주세요."),
  port: z.string().refine((value) => value === "" || (/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 65535), "포트는 1~65535 사이로 입력해 주세요."),
  pathPrefix: z.string().trim().min(1, "경로를 입력해 주세요.").max(500, "경로는 500자 이하로 입력해 주세요.").startsWith("/", "경로는 /로 시작해야 합니다."),
});
export type ReturnUrlFormValues = z.infer<typeof returnUrlFormSchema>;

export const callbackFormSchema = z.object({
  environment: z.enum(["LOCAL", "DEV", "PROD"]),
  callbackUrl: z.string().trim().min(1, "Callback URL을 입력해 주세요.").max(2000, "Callback URL은 2,000자 이하로 입력해 주세요.").url("유효한 HTTPS URL을 입력해 주세요.").refine((value) => {
    try { const url = new URL(value); return url.protocol === "https:" && Boolean(url.hostname) && !url.username && !url.password && !url.hash; } catch { return false; }
  }, "사용자 정보와 fragment가 없는 HTTPS URL을 입력해 주세요."),
});
export type CallbackFormValues = z.infer<typeof callbackFormSchema>;

export const bannerFormSchema = z.object({
  signupImageFileAssetId: z.number().int().positive().nullable(),
  signupImageAlt: z.string().trim().max(500, "이미지 설명은 500자 이하로 입력해 주세요."),
  bannerCode: z.string().trim().regex(/^[A-Za-z0-9_-]{1,80}$/, "영문, 숫자, 밑줄, 하이픈으로 80자 이하로 입력해 주세요."),
  bannerName: z.string().trim().min(1, "배너명을 입력해 주세요.").max(200, "배너명은 200자 이하로 입력해 주세요."),
  bannerType: z.string().trim().min(1, "배너 유형을 입력해 주세요.").max(30, "배너 유형은 30자 이하로 입력해 주세요."),
  placementCode: z.string().trim().min(1, "노출 위치를 입력해 주세요.").max(50, "노출 위치는 50자 이하로 입력해 주세요."),
  targetUrl: z.string().trim().max(2000, "이동 URL은 2,000자 이하로 입력해 주세요.").refine((value) => value === "" || z.string().url().safeParse(value).success, "유효한 URL을 입력해 주세요."),
  exposureStartedAt: localDateTime,
  exposureEndedAt: optionalLocalDateTime,
  displayOrder: z.string().regex(/^\d+$/, "노출 순서는 0 이상의 정수로 입력해 주세요."),
}).superRefine((value, context) => {
  if (value.exposureEndedAt && value.exposureStartedAt >= value.exposureEndedAt) context.addIssue({ code: "custom", path: ["exposureEndedAt"], message: "노출 종료일시는 시작일시보다 늦어야 합니다." });
});
export type BannerFormValues = z.infer<typeof bannerFormSchema>;

export const termFormSchema = z.object({
  termCode: z.string().trim().regex(/^[A-Za-z0-9_-]{1,80}$/, "영문, 숫자, 밑줄, 하이픈으로 80자 이하로 입력해 주세요."),
  termType: z.string().trim().min(1, "약관 유형을 입력해 주세요.").max(40, "약관 유형은 40자 이하로 입력해 주세요."),
  title: z.string().trim().min(1, "약관명을 입력해 주세요.").max(300, "약관명은 300자 이하로 입력해 주세요."),
  content: z.string().trim().min(1, "약관 내용을 입력해 주세요."),
  versionNo: z.string().trim().min(1, "버전을 입력해 주세요.").max(30, "버전은 30자 이하로 입력해 주세요."),
  required: z.boolean(),
  partnerChannelId: z.string(),
  displayOrder: z.string().regex(/^\d+$/, "노출 순서는 0 이상의 정수로 입력해 주세요."),
  effectiveFrom: localDateTime,
});
export type TermFormValues = z.infer<typeof termFormSchema>;

export const privacyFormSchema = termFormSchema.extend({
  title: z.string().trim().min(1, "개인정보처리방침 제목을 입력해 주세요.").max(300, "제목은 300자 이하로 입력해 주세요."),
  content: z.string().trim().min(1, "개인정보처리방침 본문을 입력해 주세요."),
  versionNo: z.string().trim().min(1, "버전을 입력해 주세요.").max(30, "버전은 30자 이하로 입력해 주세요."),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "시행일시를 선택해 주세요."),
});

export function parsePartnerTab(value: string | null): PartnerTab { return PARTNER_TABS.includes(value as PartnerTab) ? value as PartnerTab : "channels"; }
export function parseChannelStatus(value: string | null): ChannelStatus { return value === "ACTIVE" || value === "INACTIVE" ? value : ""; }
export function parseActiveFilter(value: string | null): ActiveFilter { return value === "true" || value === "false" ? value : ""; }
export function parsePage(value: string | null) { const parsed = Number(value); return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0; }
export function parseChannelId(value: string | null) { const parsed = Number(value); return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined; }
export function toUtcLocalDateTime(kst: string): string { return new Date(`${kst}:00+09:00`).toISOString().slice(0, 19); }
export function toKstLocalDateTime(utc?: string | null): string {
  if (!utc) return "";
  const value = /(?:Z|[+-]\d{2}:\d{2})$/.test(utc) ? utc : `${utc}Z`;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Seoul", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(date).replace(" ", "T");
}
